import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useAxios } from '../../core/_axios';
import { useAuth } from '../../AuthContext';
import { EmailCampaign, CampaignFilters } from '../../core/_models_email';
import { getHistoricalEmailCampaigns } from '../../core/_requests_email';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmailViewModal from '../../components/EmailViewModal';

const EmailHistory: React.FC = () => {
  const axios = useAxios();
  const { currentUserAccountUniqueId, viewingAllAccounts } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [filters, setFilters] = useState<CampaignFilters>({
    min_score: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // Debug logging
    console.log('EmailHistory - currentUserAccountUniqueId:', currentUserAccountUniqueId);
    console.log('EmailHistory - viewingAllAccounts:', viewingAllAccounts);
    
    // Load campaigns when we have a specific account OR when viewing all accounts
    if (currentUserAccountUniqueId || viewingAllAccounts) {
      loadCampaigns();
    } else {
      console.log('EmailHistory - No account ID and not viewing all accounts, skipping load');
      setLoading(false);
    }
  }, [filters, currentUserAccountUniqueId, viewingAllAccounts]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading campaigns with params:', {
        limit: 50,
        offset: 0,
        all_accounts: viewingAllAccounts,
        currentUserAccountUniqueId
      });
      
      const historicalCampaigns = await getHistoricalEmailCampaigns(axios, {
        limit: 50,
        offset: 0,
        all_accounts: viewingAllAccounts
      });
      
      console.log('Received campaigns:', historicalCampaigns);
      
      // Filter by score on the client side if needed
      const filtered = historicalCampaigns.filter(campaign => 
        campaign.lead_score >= (filters.min_score || 0)
      );
      
      // Sort by reviewed_at (most recent first)
      const sorted = filtered.sort((a, b) => {
        const aDate = a.reviewed_at || a.created_at;
        const bDate = b.reviewed_at || b.created_at;
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });
      
      setCampaigns(sorted);
    } catch (err: any) {
      console.error('Failed to load historical campaigns:', err);
      console.error('Error details:', {
        message: err?.message,
        response: err?.response,
        data: err?.response?.data
      });
      setError('Failed to load email history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'approved':
      case 'sent':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const getLeadScoreBadge = (score: number) => {
    if (score >= 80) return 'badge-success';
    if (score >= 60) return 'badge-warning';
    if (score >= 40) return 'badge-info';
    return 'badge-secondary';
  };

  const openViewModal = (campaign: EmailCampaign) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedCampaign(null);
    setIsModalOpen(false);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Error Alert */}
      {error && (
        <div className="container-fixed mb-4">
          <div className="alert alert-danger flex justify-between items-center">
            <span>{error}</span>
            <button 
              className="btn btn-sm btn-icon btn-light"
              onClick={() => setError(null)}
            >
              <i className="ki-filled ki-cross"></i>
            </button>
          </div>
        </div>
      )}
    
      <div className="card card-grid min-w-full">
        <div className="card-header flex-wrap py-3">
          <h3 className="card-title">
            Message History ({campaigns.length})
          </h3>
          
          <div className="flex gap-4">
            {/* Score Filter - Only show when there are campaigns */}
            {campaigns.length > 0 && (
              <select
                className="form-select form-select-sm form-select-solid"
                value={filters.min_score || 0}
                onChange={(e) => setFilters({ ...filters, min_score: parseInt(e.target.value) })}
              >
                <option value="0">All Scores</option>
                <option value="40">40+ Score</option>
                <option value="60">60+ Score</option>
                <option value="80">80+ Score</option>
              </select>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          {campaigns.length === 0 ? (
            <div className="text-center py-10">
              <i className="ki-filled ki-mail text-5xl text-gray-400 mb-5"></i>
              <h3 className="text-gray-700 text-lg font-medium mb-2">No message history yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Once you approve and send follow-up messages, they'll appear here for your records.
              </p>
              <Link 
                to="/follow-up-messages/manual-draft" 
                className="btn btn-primary"
              >
                <i className="ki-filled ki-plus-circle mr-2"></i>
                Create Your First Message
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-border">
                <thead>
                  <tr className="text-gray-600 fw-medium">
                    {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                      <th className="min-w-[150px]">Account</th>
                    )}
                    <th className="min-w-[200px]">Signer</th>
                    <th className="min-w-[150px]">Service</th>
                    <th className="min-w-[300px]">Email</th>
                    <th className="w-[100px]">Status</th>
                    <th className="w-[120px]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr 
                      key={campaign.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openViewModal(campaign)}
                    >
                      {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                        <td>
                          <div className="text-gray-800 fw-medium text-sm">
                            {campaign.account_name || 'Unknown Account'}
                          </div>
                        </td>
                      )}
                      <td>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-1">
                            <span className="text-gray-800 fw-medium text-sm">
                              {campaign.signer_info?.name || 'Unknown'}
                            </span>
                            <span className={`badge ${getLeadScoreBadge(campaign.lead_score)} badge-sm ms-3`}>
                              {campaign.lead_score}
                            </span>
                          </div>
                          <div className="text-gray-600 text-xs">
                            {campaign.signer_info?.email || 'No email'}
                            {campaign.signer_info?.phone && (
                              <span className="ms-2">• {campaign.signer_info.phone}</span>
                            )}
                          </div>
                          {(campaign.lead_score_breakdown?.interested_in_pre_need > 0 || campaign.is_dry_run) && (
                            <div className="d-flex gap-1 mt-1">
                              {campaign.lead_score_breakdown?.interested_in_pre_need > 0 && (
                                <span className="badge badge-light-primary badge-sm">Pre-Need</span>
                              )}
                              {campaign.is_dry_run && (
                                <span className="badge badge-light-warning badge-sm">Dry Run</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="text-gray-800 fw-medium text-sm">
                          {campaign.service_unique_identifier ? (
                            <Link 
                              to={`/services/${campaign.service_unique_identifier}`}
                              className="text-primary hover:text-primary-dark"
                            >
                              {campaign.deceased_info?.name || campaign.service_name || 'Unknown'}
                            </Link>
                          ) : (
                            campaign.deceased_info?.name || campaign.service_name || 'Unknown'
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <div className="text-gray-800 fw-medium text-sm mb-1">
                            {campaign.email_subject}
                          </div>
                          <div className="text-gray-600 text-xs">
                            {campaign.final_email_content 
                              ? campaign.final_email_content.length > 80 
                                ? `${campaign.final_email_content.substring(0, 80)}...`
                                : campaign.final_email_content
                              : 'No content'
                            }
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(campaign.status)} badge-sm`}>
                          {campaign.status === 'approved' ? 'Approved' : 
                           campaign.status === 'rejected' ? 'Rejected' : 
                           campaign.status === 'sent' ? 'Sent' : 
                           campaign.status}
                        </span>
                      </td>
                      <td className="text-gray-600 text-xs">
                        {(() => {
                          const dateStr = campaign.reviewed_at || campaign.created_at;
                          const dateTime = DateTime.fromISO(dateStr);
                          const relativeTime = dateTime.toRelative();
                          const fullDateTime = dateTime.toFormat('LLL d, yyyy \'at\' h:mm a');
                          
                          return (
                            <span title={fullDateTime}>
                              {relativeTime}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      <EmailViewModal
        campaign={selectedCampaign}
        isOpen={isModalOpen}
        onClose={closeViewModal}
      />
    </>
  );
};

export default EmailHistory;